/*
 * Putting invoices together and taking a visit back off (old invmerge.php). The server and the demo both check with these.
 *
 * - Invoices of one client become one. They all wait in To check, or one of them was sent and is not paid yet (the old
 *   Add to invoice: a job finished after the client's invoice went out). The sent one keeps its number and pay link and
 *   shows the new total; nothing is sent. Otherwise the oldest one is kept. The others are voided.
 * - Only invoices with no money on them go onto another. Deposits, progress payments and agency bills never merge.
 * - Changing a sent invoice needs the owner or a manager (invoice.edit_paid), like any other change to a sent invoice.
 * - A visit comes off an invoice with more than one visit, while it is in To check or sent and not paid. Its lines go on
 *   a new invoice in To check. The invoice it leaves can't drop below what was already paid on it.
 */

export interface MergeOne {
  id: string;
  clientId: string;
  status: string;
  /** Money paid on it so far (cents). */
  paidCents: number;
  /** progress, signing, retainer: never merged. */
  billKind: string | null;
  /** A quote deposit. */
  quoteId: string | null;
}

export type MergeCheck = { ok: true; keepId: string } | { ok: false; status: number; code: string; message: string };

const no = (status: number, code: string, message: string): MergeCheck => ({ ok: false, status, code, message });

/** Which invoice the others go on, or why they can't. The list is oldest first. */
export function mergeCheck(list: readonly MergeOne[], mayEditSent: boolean): MergeCheck {
  if (list.length < 2) return no(422, "merge_two", "Pick at least two invoices.");
  if (new Set(list.map((i) => i.clientId)).size > 1)
    return no(409, "merge_one_client", "Those invoices are for different clients.");
  if (list.some((i) => i.billKind !== null || i.quoteId !== null))
    return no(409, "merge_special", "Deposits, progress payments and agency bills stay on their own.");
  if (list.some((i) => i.status !== "to_check" && i.status !== "sent"))
    return no(409, "merge_to_check_only", "Only invoices in To check, or one sent and not paid yet, can be merged.");
  const sent = list.filter((i) => i.status === "sent");
  if (sent.length > 1) return no(409, "merge_one_sent", "Only one of them can be sent already. Pick one sent invoice.");
  const keep = sent[0] ?? list[0]!;
  if (list.some((i) => i.id !== keep.id && i.paidCents !== 0))
    return no(
      409,
      "merge_has_payments",
      "One of them has a payment on it. Only an invoice with no money on it can go on another.",
    );
  if (keep.status === "sent" && !mayEditSent)
    return no(403, "invoice_sent", "One of them was sent. Ask the owner or a manager to add to it.");
  return { ok: true, keepId: keep.id };
}

export interface TakeOffOne {
  status: string;
  billKind: string | null;
  /** Visits on the invoice now. */
  visitCount: number;
  /** What the lines left behind add up to (cents). */
  leftCents: number;
  /** How many lines are left behind. */
  leftLines: number;
  tipCents: number;
  paidCents: number;
}

export type TakeOffCheck = { ok: true } | { ok: false; status: number; code: string; message: string };

/** Whether a visit can come off this invoice. */
export function takeOffCheck(i: TakeOffOne, mayEditSent: boolean): TakeOffCheck {
  const no2 = (status: number, code: string, message: string): TakeOffCheck => ({ ok: false, status, code, message });
  if (i.status !== "to_check" && i.status !== "sent")
    return no2(409, "take_off_open_only", "A visit can only come off an invoice in To check, or sent and not paid yet.");
  if (i.billKind !== null) return no2(409, "merge_special", "Progress payments and agency bills stay as they are.");
  if (i.visitCount < 2) return no2(409, "take_off_one_visit", "This invoice has only one visit. Void it instead.");
  if (i.leftLines < 1) return no2(422, "invoice_empty", "The invoice would have no lines left.");
  if (i.status === "sent" && !mayEditSent)
    return no2(403, "invoice_sent", "This invoice was sent. Ask the owner or a manager to change it.");
  if (i.leftCents + i.tipCents < i.paidCents)
    return no2(422, "below_paid", "Taking it off would leave less than what was already paid.");
  return { ok: true };
}
